import React from "react";
import {
  Box,
  Button,
  Container,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import Basket from "./Basket";
import { CartItem } from "../../../lib/types/search";
import { useGlobals } from "../../hooks/useGlobals";
import { serverApi } from "../../../lib/config";
import { Logout } from "@mui/icons-material";

interface HomeNavbarPorps {
  cartItems: CartItem[];
  onAdd: (item: CartItem) => void;
  onRemove: (item: CartItem) => void;
  onDelete: (item: CartItem) => void;
  onDeleteAll: () => void;
  setSignupOpen: (isOpen: boolean) => void;
  setLoginOpen: (isOpen: boolean) => void;
  handleLogoutClick: (e: React.MouseEvent<HTMLElement>) => void;
  anchorEl: HTMLElement | null;
  handleCloseLogout: () => void;
  handleLogoutRequest: () => void;
}

export default function HomeNavbar(props: HomeNavbarPorps) {
  const {
    cartItems,
    onAdd,
    onRemove,
    onDelete,
    onDeleteAll,
    setSignupOpen,
    setLoginOpen,
    handleLogoutClick,
    anchorEl,
    handleCloseLogout,
    handleLogoutRequest,
  } = props;
  const { authMember } = useGlobals();
  const memberImage = authMember?.memberImage
    ? `${serverApi}/${authMember.memberImage}`
    : "/icons/default-user.svg";

  return (
    <div className={"home-navbar"}>
      <Container className={"navbar-container"}>
        <Stack className={"menu"}>
          <NavLink to={"/"} className={"brand"}>
            <img className={"brand-logo"} src="/icons/Booksaw.svg" alt="Booksaw" />
          </NavLink>

          <Stack className={"links"}>
            <Box className={"hover-line"}>
              <NavLink to="/" activeClassName={"underline"}>
                Home
              </NavLink>
            </Box>
            <Box className={"hover-line"}>
              <NavLink to="/products" activeClassName={"underline"}>
                Products
              </NavLink>
            </Box>
            {authMember ? (
              <Box className={"hover-line"}>
                <NavLink to="/orders" activeClassName={"underline"}>
                  Orders
                </NavLink>
              </Box>
            ) : null}
            {authMember ? (
              <Box className={"hover-line"}>
                <NavLink to="/member-page" activeClassName={"underline"}>
                  My Page
                </NavLink>
              </Box>
            ) : null}
            <Box className={"hover-line"}>
              <NavLink to="/help" activeClassName={"underline"}>
                Help
              </NavLink>
            </Box>
          </Stack>

          <Stack className={"link-basket"}>
            <Basket
              cartItems={cartItems}
              onAdd={onAdd}
              onRemove={onRemove}
              onDelete={onDelete}
              onDeleteAll={onDeleteAll}
            />
            {!authMember ? (
              <Stack className={"auth-actions"}>
                <Button
                  variant="contained"
                  className={"login-button ghost"}
                  onClick={() => setLoginOpen(true)}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  className={"join-button"}
                  onClick={() => setSignupOpen(true)}
                >
                  Join free
                </Button>
              </Stack>
            ) : (
              <img
                className={"user-avatar"}
                src={memberImage}
                alt="user"
                onClick={handleLogoutClick}
              />
            )}
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={Boolean(anchorEl)}
              onClose={handleCloseLogout}
              onClick={handleCloseLogout}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={handleLogoutRequest}>
                <ListItemIcon>
                  <Logout fontSize="small" style={{ color: "#8a4f1d" }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        <Stack className={"header-frame"}>
          <Stack className={"detail"}>
            <span className={"hero-eyebrow"}>Curated online bookstore</span>
            <Box className={"head-main-txt"}>
              Find your next great read without the noise.
            </Box>
            <Box className={"wel-txt"}>
              A calm digital bookstore for bestsellers, kids stories, and
              self-growth picks — curated so every shelf feels useful.
            </Box>
            <Stack className={"hero-search-card"}>
              <span>Search by mood, author, or title</span>
              <NavLink to="/products">Browse collection</NavLink>
            </Stack>
            <Stack className={"hero-actions"}>
              <NavLink to="/products" className={"service-txt"}>
                Shop books <img src="/icons/arrow-right.svg" alt="" />
              </NavLink>
              {!authMember ? (
                <Button
                  variant={"contained"}
                  className={"signup-button"}
                  onClick={() => setSignupOpen(true)}
                >
                  Create account
                </Button>
              ) : null}
            </Stack>
            <Stack className={"hero-meta"}>
              <span>Bestsellers</span>
              <span>Kids corner</span>
              <span>Self-growth</span>
            </Stack>
          </Stack>

          <Stack className={"logo-frame"}>
            <div className={"hero-orbit one"} />
            <div className={"hero-orbit two"} />
            <Stack className={"hero-book-card"}>
              <span className={"book-label"}>Featured pick</span>
              <img src="/img/atomicH.jpeg" alt="" />
              <Stack className={"book-info"}>
                <strong>Atomic Habits</strong>
                <span>Small changes, remarkable results.</span>
              </Stack>
            </Stack>
            <Stack className={"hero-mini-book left"}>
              <span>01</span>
              <strong>New releases</strong>
            </Stack>
            <Stack className={"hero-mini-book right"}>
              <span>4.9</span>
              <strong>Reader score</strong>
            </Stack>
            <Stack className={"floating-card top"}>Fresh picks every week</Stack>
            <Stack className={"floating-card bottom"}>10K+ readers</Stack>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
}
